import { useCallback, useEffect } from 'react';
import { useReducerAsync, AsyncActionHandlers } from 'use-reducer-async';

import logger from '../../config/app.logger';
import { getExtendedLogger } from '../Logger';

import { getTranslations } from './helpers';
import { ActionTypes, LOGGER_NAME } from './constants';

import { TActions, TState, TChangeLanguageAction } from './types';

const log = getExtendedLogger(LOGGER_NAME, logger);

const initialState = {
  loaded: false,
  loading: true,
  failed: false,
  language: 'en',
  translations: {} as Record<string, string>,
} as TState;

function reducer(state: TState, action: TActions) {
  switch (action.type) {
    case ActionTypes.Loading:
      return { ...state, loaded: false, loading: true, failed: true };
    case ActionTypes.Failed:
      return { ...state, loaded: false, loading: false, failed: true };
    case ActionTypes.Loaded:
      return {
        ...state,
        loaded: true,
        loading: false,
        failed: false,
        language: action.payload.locale,
        translations: action.payload.translations,
      };
    case ActionTypes.SetLoadingFalse:
      return {
        ...state,
        loading: false,
      };
    default:
      return state;
  }
}

const asyncActionHandlers = {
  CHANGE_LANGUAGE: ({ dispatch }) => async action => {
    if (!(action.payload.localeBaseUrl && action.payload.locale)) {
      dispatch({ type: ActionTypes.SetLoadingFalse });

      return;
    }
    dispatch({ type: ActionTypes.Loading });
    try {
      log(
        'loading translation for %s language from %s url',
        action.payload.locale,
        action.payload.localeBaseUrl
      );

      const translations = await getTranslations(
        action.payload.localeBaseUrl,
        action.payload.locale
      );

      log('Successfully loaded translation %o', translations);

      dispatch({
        type: ActionTypes.Loaded,
        payload: { translations, locale: action.payload.locale },
      });
    } catch (error) {
      log('Failed to load translation');
      dispatch({
        type: ActionTypes.Failed,
        payload: { error },
      });
    }
  },
} as AsyncActionHandlers<typeof reducer, TChangeLanguageAction>;

function useTranslations(locale: string, localeBaseUrl?: string) {
  const [state, dispatch] = useReducerAsync(reducer, initialState, asyncActionHandlers);

  const changeLanguage = useCallback(() => {
    log('changing language to %s', locale);
    dispatch({ type: ActionTypes.ChangeLanguage, payload: { locale, localeBaseUrl } });
  }, [dispatch, locale, localeBaseUrl]);

  useEffect(changeLanguage, [changeLanguage]);

  return {
    translations: state.translations,
    loaded: state.loaded,
    loading: state.loading,
    failed: state.failed,
  };
}

export default useTranslations;
